import React, { ChangeEvent, useMemo, useState } from "react";

import { Box, Checkbox, FormLabel, Radio, RadioGroup, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import MenuItemOptionGroupType from "api/types/MenuItemOptionGroup";
import Price from "components/Price/Price";

import SelectedOption from "./SelectedOption";

const useStyles = makeStyles(theme => {
	return {
		optionGroupName: {
			textTransform: "uppercase",
			paddingBottom: theme.spacing(1),
			[theme.breakpoints.up(480)]: {
				fontWeight: "500"
			}
		},
		radioGroup: {
			display: "flex",
			flex: 1,
			padding: 0,
			flexFlow: "column",
			width: "calc(var(--vw, 1vw) * 100 - 32px)", // not great..
			[theme.breakpoints.up(480)]: {
				// width: "auto",
				width: "100%",
				color: theme.palette.text.primary
			}
		},
		radio: {
			color: "#f2f2f2",
			"&$checked": {
				color: theme.palette.primary.main,
				[theme.breakpoints.up(480)]: {
					color: theme.palette.text.primary
				}
			},
			borderColor: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary,
				borderColor: "#fff"
			}
		},
		itemContent: {
			display: "flex",
			flexDirection: "row"
		},
		itemCode: {
			textAlign: "left",
			marginRight: theme.spacing(1)
		},
		itemName: {
			textAlign: "left"
		},

		option: {
			paddingLeft: theme.spacing(2),
			paddingRight: theme.spacing(1),
			borderTop: "1px solid #f2f2f2",
			fontSize: 12,
			color: "#fff",
			alignContent: "center",
			alignItems: "center",
			display: "flex",
			justifyContent: "space-between",
			[theme.breakpoints.up(480)]: {
				flexDirection: "row-reverse",
				color: theme.palette.text.primary,
				paddingLeft: 0
			}
		},
		optionContainer: {
			textTransform: "uppercase",
			width: "100%",
			display: "flex",
			alignContent: "center",
			alignItems: "center",
			flex: 1,
			justifyContent: "space-between"
		}
	};
});

interface MenuItemOptionGroupProps {
	menuItemOptionGroup: MenuItemOptionGroupType;

	defaultValue?: SelectedOption[];

	onChange?(event: { menuItemOptionGroup: MenuItemOptionGroupType; selection: null | SelectedOption[] }): void;
}

const MenuItemOptionGroup: React.FC<MenuItemOptionGroupProps> = ({ menuItemOptionGroup, defaultValue, onChange }) => {
	const classes = useStyles();

	const [value, setValue] = useState<null | string[]>(
		menuItemOptionGroup.menuItemOptions
			.filter(x =>
				defaultValue?.find(y => y.menuItemOptionGroupId === menuItemOptionGroup.id && y.menuItemOptionId === x.id)
			)
			?.map(a => a.id) || null
	);

	const handleChange = useMemo(
		() => (event: ChangeEvent<HTMLInputElement>) => {
			const selectedValue = event.currentTarget.value;
			const option = menuItemOptionGroup.menuItemOptions.find(x => x.id === selectedValue);

			setValue(option ? [option.id] : null);

			onChange &&
				onChange({
					menuItemOptionGroup,
					selection: option
						? [
								{
									menuItemOptionGroupId: menuItemOptionGroup.id,
									menuItemOptionId: option.id,
									priceCents: option.priceCents || undefined,
									displayPrice: option.displayPrice || undefined,
									preparationTimeMinutes: option.preparationTimeMinutes || undefined
								}
						  ]
						: null
				});
		},
		[menuItemOptionGroup, onChange]
	);

	const handleCheckboxChange = (selectedValue: string) => {
		let values: string[] = value || [];
		if (values?.find(a => a === selectedValue)) {
			values = values?.filter(a => a != selectedValue);
		} else {
			values = [...values, selectedValue];
		}
		setValue(values);
		const options = menuItemOptionGroup.menuItemOptions
			.filter(x => values.find(a => a === x.id))
			.map(option => {
				return {
					menuItemOptionGroupId: menuItemOptionGroup.id,
					menuItemOptionId: option.id,
					priceCents: option.priceCents || undefined,
					displayPrice: option.displayPrice || undefined,
					preparationTimeMinutes: option.preparationTimeMinutes || undefined
				};
			});
		onChange &&
			onChange({
				menuItemOptionGroup,
				selection: options
			});
	};

	if (menuItemOptionGroup?.minCount === 1 && menuItemOptionGroup?.maxCount === 1) {
		// This means radio options
		return (
			<RadioGroup className={classes.radioGroup} aria-label="option" value={value} onChange={handleChange}>
				<Typography className={classes.optionGroupName}>{menuItemOptionGroup.name}</Typography>
				{menuItemOptionGroup.menuItemOptions.map(o => (
					<FormLabel className={classes.option} key={o.id}>
						<Box className={classes.optionContainer}>
							<Box className={classes.itemContent}>
								{o.itemCode && <Box className={classes.itemCode}>{o.itemCode}</Box>}

								<Box className={classes.itemName}>{o.name}</Box>
							</Box>
							{!!o.priceCents && <Price priceCents={o.priceCents} />}
						</Box>

						<Radio
							inputProps={{ "aria-label": o.name }}
							classes={{ root: classes.radio }}
							value={o.id}
							checked={!!value?.find(a => a === o.id)}
							color="secondary"
							disabled={!!o.outOfStock}
						/>
					</FormLabel>
				))}
			</RadioGroup>
		);
	} else {
		return (
			<Box className={classes.radioGroup} aria-label="option">
				<Typography className={classes.optionGroupName}>{menuItemOptionGroup.name}</Typography>
				{menuItemOptionGroup.menuItemOptions.map(o => (
					<FormLabel className={classes.option} key={o.id}>
						<Box className={classes.optionContainer}>
							<Box className={classes.itemContent}>
								{o.itemCode && <Box className={classes.itemCode}>{o.itemCode}</Box>}

								<Box className={classes.itemName}>{o.name}</Box>
							</Box>
							{!!o.priceCents && <Price priceCents={o.priceCents} />}
						</Box>
						<Checkbox
							inputProps={{ "aria-label": o.name }}
							classes={{ root: classes.radio }}
							checked={!!value?.find(a => a === o.id)}
							value={o.id}
							color="secondary"
							onChange={() => handleCheckboxChange(o.id)}
							disabled={!!o.outOfStock}
						/>
					</FormLabel>
				))}
			</Box>
		);
	}
};

export default MenuItemOptionGroup;
